import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiSolidInbox } from 'react-icons/bi';
import { useAuth } from '@/auth/AuthContext';
import { getRoles, listUsers, updateUser } from '@/api/users.api';
import { ApiError } from '@/api/errors';
import { Banner, type BannerTone } from '@/components/common/Banner';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { StatusChip } from '@/components/common/StatusChip';
import {
  DataTable,
  type ActiveFilterChip,
  type DataTableColumn,
  type DataTableSortState,
} from '@/components/table/DataTable';
import { EmptyState } from '@/components/table/EmptyState';
import { formatDate, formatDateTime } from '@/lib/format';
import { lookupLabel } from '@/lib/lookups';
import { ResetPasswordDialog } from '@/pages/settings/ResetPasswordDialog';
import { UserFormPanel } from '@/pages/settings/UserFormPanel';
import { showSuccessToast } from '@/lib/toast';
import type { PaginatedUsers, Role, UserResponse } from '@/types/shared';
import './UsersPage.css';

type StatusFilter = '' | 'active' | 'inactive';

/**
 * Settings → Users (`/settings/users`, Admin-only — gated by `RoleGuard` in `router.tsx`, T71).
 *
 * Deactivate-vs-delete model: this screen's "Deactivate" row action is `PATCH /users/{id}` with
 * `isActive: false` — **not** `DELETE` (soft-delete). Reasons:
 *   1. `GET /users` unconditionally excludes soft-deleted rows (no `withDeleted` option, and this
 *      contract deliberately has no "show deleted" toggle), so a soft-deleted user would vanish
 *      from every view with no way to find/restore them from this screen.
 *   2. The `isActive` filter below lets an Admin still find and reactivate a deactivated account
 *      at any time — a fully reversible, visible state, which is the expected semantics of
 *      "deactivate" (temporarily disable sign-in) vs. "delete" (remove the record).
 *   3. The lockout guard (`CANNOT_DEACTIVATE_SELF` / `LAST_ACTIVE_ADMIN`) is enforced identically
 *      on both `PATCH isActive:false` and `DELETE` server-side, so nothing is lost by preferring
 *      the reversible path.
 * `DELETE` + `POST /:id/restore` remain in `users.api.ts` for a possible future "deleted users"
 * view, but are intentionally not wired into this UI.
 */
export function UsersPage() {
  const { t, i18n } = useTranslation();
  const { user: currentUser } = useAuth();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState<DataTableSortState>({ field: null, direction: 'asc' });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<Role | ''>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');

  const [roles, setRoles] = useState<Role[]>([]);

  const [data, setData] = useState<PaginatedUsers | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [banner, setBanner] = useState<{ tone: BannerTone; text: string } | null>(null);

  const [panel, setPanel] = useState<
    { mode: 'create' } | { mode: 'edit'; user: UserResponse } | null
  >(null);

  const [confirmTarget, setConfirmTarget] = useState<{
    user: UserResponse;
    action: 'activate' | 'deactivate';
  } | null>(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  const [resetPasswordTarget, setResetPasswordTarget] = useState<UserResponse | null>(null);

  // Debounce the free-text search box before it drives the `search` query param.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Any filter/search change re-starts pagination at page 1 (sort changes keep the current page).
  useEffect(() => {
    setPage(1);
  }, [search, roleFilter, statusFilter]);

  useEffect(() => {
    getRoles()
      .then(setRoles)
      .catch(() => setRoles([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const sortParam = sort.field ? `${sort.field}:${sort.direction.toUpperCase()}` : undefined;

    listUsers({
      page,
      pageSize,
      sort: sortParam,
      search: search || undefined,
      role: roleFilter === '' ? undefined : roleFilter,
      isActive: statusFilter === '' ? undefined : statusFilter === 'active',
    })
      .then((result) => {
        if (cancelled) return;
        setData(result);
      })
      .catch(() => {
        if (cancelled) return;
        setError(t('usersSettings.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, sort, search, roleFilter, statusFilter, t, reloadToken]);

  function refetch() {
    setReloadToken((v) => v + 1);
  }

  const activeFilterCount = [Boolean(search), roleFilter !== '', statusFilter !== ''].filter(
    Boolean,
  ).length;
  const hasActiveFilters = activeFilterCount > 0;

  function clearFilters() {
    setSearchInput('');
    setSearch('');
    setRoleFilter('');
    setStatusFilter('');
  }

  const activeFilterChips: ActiveFilterChip[] = [
    roleFilter !== '' && {
      key: 'role',
      label: t('usersSettings.filters.role'),
      valueLabel: roleFilter,
      onRemove: () => setRoleFilter(''),
    },
    statusFilter !== '' && {
      key: 'status',
      label: t('usersSettings.filters.status'),
      valueLabel:
        statusFilter === 'active'
          ? t('usersSettings.filters.active')
          : t('usersSettings.filters.inactive'),
      onRemove: () => setStatusFilter(''),
    },
  ].filter(Boolean) as ActiveFilterChip[];

  function openCreatePanel() {
    setBanner(null);
    setPanel({ mode: 'create' });
  }

  function openEditPanel(user: UserResponse) {
    setBanner(null);
    setPanel({ mode: 'edit', user });
  }

  function closePanel() {
    setPanel(null);
  }

  function handleFormSuccess(_user: UserResponse, message: string) {
    setBanner({ tone: 'success', text: message });
    showSuccessToast(message);
    setPanel(null);
    refetch();
  }

  function genericErrorMessage(apiError: ApiError): string {
    if (apiError.code === 'NETWORK_ERROR') return t('usersSettings.form.networkError');
    if (apiError.code === 'CANNOT_DEACTIVATE_SELF') return t('usersSettings.cannotDeactivateSelf');
    if (apiError.code === 'LAST_ACTIVE_ADMIN') return t('usersSettings.lastActiveAdminError');
    return apiError.message || t('usersSettings.form.genericError');
  }

  async function handleConfirm() {
    if (!confirmTarget) return;
    setConfirmSubmitting(true);
    try {
      const updated = await updateUser(confirmTarget.user.id, {
        isActive: confirmTarget.action === 'activate',
      });
      const message =
        confirmTarget.action === 'activate'
          ? t('usersSettings.activateConfirm.success', { name: updated.fullName })
          : t('usersSettings.deactivateConfirm.success', { name: updated.fullName });
      setBanner({
        tone: 'success',
        text: message,
      });
      showSuccessToast(message);
      setConfirmTarget(null);
      refetch();
    } catch (err) {
      setBanner({ tone: 'danger', text: genericErrorMessage(ApiError.fromUnknown(err)) });
      setConfirmTarget(null);
    } finally {
      setConfirmSubmitting(false);
    }
  }

  function handleResetPasswordSuccess(message: string) {
    setResetPasswordTarget(null);
    setBanner({ tone: 'success', text: message });
    showSuccessToast(message);
  }

  const columns: DataTableColumn<UserResponse>[] = [
    {
      key: 'fullName',
      header: t('usersSettings.columns.fullName'),
      sortField: 'fullName',
      // Primary identifier column — always rendered, never hidden (14-...-spec.md §4.2).
      toggleable: false,
      render: (row) => <bdi>{row.fullName}</bdi>,
    },
    {
      key: 'email',
      header: t('usersSettings.columns.email'),
      sortField: 'email',
      render: (row) => <bdi className="fa-ltr-isolate">{row.email}</bdi>,
    },
    {
      key: 'role',
      header: t('usersSettings.columns.role'),
      sortField: 'role',
      render: (row) => row.role,
    },
    {
      // T174 (§20, DEC-BR2, `docs/design/19-branch-scoping-ux.md` §2.5) — placed after Role, before
      // Status: the two "what can this user do/see" columns, grouped adjacently. Never hidden
      // regardless of the viewing Admin's own scope (`/settings/users` is Admin-only, and Admin is
      // always all-branches — §2.5's own note).
      key: 'branchAccess',
      header: t('usersSettings.columns.branchAccess'),
      render: (row) => {
        // "All branches" and "every branch individually ticked" must never render the same way
        // (T159 §2.1) — a distinct `info`-tone chip, never a chip-list that happens to be long.
        if (row.allBranches) {
          return <StatusChip label={t('usersSettings.branchAccessAllChip')} tone="info" />;
        }
        const names = row.branches.map((branch) => lookupLabel(branch, i18n.language));
        if (names.length <= 2) {
          return <bdi>{names.join(', ')}</bdi>;
        }
        // >= 3 branches: first name + "+N more"; the full list is also always available via Edit
        // (never *only* a hover-only tooltip — keyboard/screen-reader users need another way in).
        return (
          <bdi title={names.join(', ')}>
            {names[0]} {t('usersSettings.branchAccessMoreCount', { count: names.length - 1 })}
          </bdi>
        );
      },
    },
    {
      key: 'status',
      header: t('usersSettings.columns.status'),
      sortField: 'isActive',
      render: (row) => (
        <StatusChip
          label={
            row.isActive ? t('usersSettings.status.active') : t('usersSettings.status.inactive')
          }
          tone={row.isActive ? 'success' : 'neutral'}
        />
      ),
    },
    {
      key: 'lastLoginAt',
      header: t('usersSettings.columns.lastLogin'),
      sortField: 'lastLoginAt',
      render: (row) =>
        row.lastLoginAt ? (
          <bdi>{formatDateTime(row.lastLoginAt, i18n.language)}</bdi>
        ) : (
          t('usersSettings.never')
        ),
    },
    {
      key: 'createdAt',
      header: t('usersSettings.columns.createdAt'),
      sortField: 'createdAt',
      render: (row) => <bdi>{formatDate(row.createdAt, i18n.language)}</bdi>,
    },
    {
      key: 'actions',
      header: t('usersSettings.columns.actions'),
      cellClassName: 'fa-users-page__actions-cell',
      // Row-actions column — always rendered, never hidden (14-...-spec.md §4.2).
      toggleable: false,
      render: (row) => {
        const isSelf = row.id === currentUser?.id;
        return (
          <div className="fa-users-page__row-actions">
            <button
              type="button"
              className="fa-btn fa-btn--ghost"
              aria-label={t('usersSettings.editAriaLabel', { name: row.fullName })}
              onClick={() => openEditPanel(row)}
            >
              {t('usersSettings.editButton')}
            </button>
            {row.isActive ? (
              <button
                type="button"
                className="fa-btn fa-btn--secondary"
                aria-label={t('usersSettings.deactivateAriaLabel', { name: row.fullName })}
                title={isSelf ? t('usersSettings.cannotDeactivateSelf') : undefined}
                disabled={isSelf}
                onClick={() => setConfirmTarget({ user: row, action: 'deactivate' })}
              >
                {t('usersSettings.deactivateButton')}
              </button>
            ) : (
              <button
                type="button"
                className="fa-btn fa-btn--secondary"
                aria-label={t('usersSettings.activateAriaLabel', { name: row.fullName })}
                onClick={() => setConfirmTarget({ user: row, action: 'activate' })}
              >
                {t('usersSettings.activateButton')}
              </button>
            )}
            <button
              type="button"
              className="fa-btn fa-btn--ghost"
              aria-label={t('usersSettings.resetPasswordAriaLabel', { name: row.fullName })}
              title={isSelf ? t('usersSettings.resetPasswordSelfHint') : undefined}
              disabled={isSelf}
              onClick={() => setResetPasswordTarget(row)}
            >
              {t('usersSettings.resetPasswordButton')}
            </button>
          </div>
        );
      },
    },
  ];

  const emptyState = hasActiveFilters ? (
    <EmptyState
      icon={BiSolidInbox}
      title={t('usersSettings.empty.noMatchesTitle')}
      hint={t('usersSettings.empty.noMatchesHint')}
      action={
        <button type="button" className="fa-btn fa-btn--secondary" onClick={clearFilters}>
          {t('usersSettings.empty.clearSearch')}
        </button>
      }
    />
  ) : (
    <EmptyState
      icon={BiSolidInbox}
      title={t('usersSettings.empty.noUsersTitle')}
      hint={t('usersSettings.empty.noUsersHint')}
    />
  );

  return (
    <section className="fa-users-page">
      <div className="fa-users-page__header">
        <div>
          <h1 className="fa-users-page__title">{t('usersSettings.pageTitle')}</h1>
          <p className="fa-users-page__description">{t('usersSettings.pageDescription')}</p>
        </div>
        {!panel && (
          <button type="button" className="fa-btn fa-btn--primary" onClick={openCreatePanel}>
            {t('usersSettings.newUserButton')}
          </button>
        )}
      </div>

      {banner && (
        <Banner tone={banner.tone} assertive={banner.tone === 'danger'}>
          {banner.text}
        </Banner>
      )}

      {panel && (
        <UserFormPanel
          mode={panel.mode}
          user={panel.mode === 'edit' ? panel.user : undefined}
          roles={roles}
          currentUserId={currentUser?.id}
          onCancel={closePanel}
          onSuccess={handleFormSuccess}
        />
      )}

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        caption={t('usersSettings.tableCaption')}
        meta={data?.meta ?? null}
        loading={loading}
        error={error}
        onRetry={refetch}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          placeholder: t('usersSettings.searchPlaceholder'),
          ariaLabel: t('usersSettings.searchAriaLabel'),
        }}
        sort={sort}
        onSortChange={setSort}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        showExportButton={false}
        columnVisibility={{
          storageKey: 'users-table-columns',
          ariaLabel: t('table.columns.toggleButtonAriaLabel'),
          drawerTitle: t('table.columns.drawerTitleDefault'),
        }}
        activeFilters={activeFilterChips}
        onClearAllFilters={clearFilters}
        filtersSlot={
          <div className="fa-users-page__filters-group">
            <label className="fa-users-page__filter">
              <span className="fa-users-page__filter-label">{t('usersSettings.filters.role')}</span>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as Role | '')}
              >
                <option value="">{t('usersSettings.filters.allRoles')}</option>
                {roles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </label>
            <label className="fa-users-page__filter">
              <span className="fa-users-page__filter-label">
                {t('usersSettings.filters.status')}
              </span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              >
                <option value="">{t('usersSettings.filters.allStatuses')}</option>
                <option value="active">{t('usersSettings.filters.active')}</option>
                <option value="inactive">{t('usersSettings.filters.inactive')}</option>
              </select>
            </label>
          </div>
        }
        emptyState={emptyState}
      />

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title={
          confirmTarget?.action === 'activate'
            ? t('usersSettings.activateConfirm.title')
            : t('usersSettings.deactivateConfirm.title')
        }
        description={
          confirmTarget
            ? confirmTarget.action === 'activate'
              ? t('usersSettings.activateConfirm.body', { name: confirmTarget.user.fullName })
              : t('usersSettings.deactivateConfirm.body', { name: confirmTarget.user.fullName })
            : undefined
        }
        confirmLabel={
          confirmTarget?.action === 'activate'
            ? t('usersSettings.activateConfirm.confirm')
            : t('usersSettings.deactivateConfirm.confirm')
        }
        cancelLabel={t('usersSettings.deactivateConfirm.cancel')}
        destructive={confirmTarget?.action === 'deactivate'}
        confirmDisabled={confirmSubmitting}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmTarget(null)}
      />

      <ResetPasswordDialog
        target={
          resetPasswordTarget
            ? { id: resetPasswordTarget.id, fullName: resetPasswordTarget.fullName }
            : null
        }
        onClose={() => setResetPasswordTarget(null)}
        onSuccess={handleResetPasswordSuccess}
      />
    </section>
  );
}
